/**
 * Attribution capture — reads UTM params from the initial landing URL and
 * stashes them in storage so the Auth0 callback can include them in the
 * /api/auth0/verify POST body.
 *
 * Why a side-effectful helper and not a hook? Because the landing URL's
 * query string is lost by the time React Router's Auth0Callback component
 * mounts (the URL has been rewritten to /callback?code=...&state=...).
 * We have to grab the UTMs on the *very first* render, before anything
 * else touches window.location.search.
 *
 * Flow:
 *   1. App bootstraps → call captureAttributionFromLandingURL() ASAP.
 *   2. User completes Auth0 → CallbackScreen reads pending attribution.
 *   3. CallbackScreen POSTs to /api/auth0/verify with { attribution: {...} }.
 *   4. Backend routers/auth0_auth.py resolves channel + persists to
 *      signup_attribution table.
 *
 * Storage is cleared after read so re-auth flows don't mis-attribute.
 * Max age safety: 7 days — after that the stashed payload is discarded.
 */

import { Platform } from 'react-native';
import { storage } from '../services/storage';
import { isClickId } from '../services/partnerAttribution';

const STORAGE_KEY = 'pending_signup_attribution';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Wave 8 partner attribution on the WEB app: the marketing site's `/go/<tag>`
 * page keeps an opaque click id and passes it to app.coachcedar.com as
 * `?pc=<id>` (desktop "Start Your Free Trial"). The Auth0 callback forwards
 * this record as `attribution.partner`; the backend's `bind_signup` accepts
 * `partner_click_id` + `capture_method` and binds the signup ONCE (first
 * source sticks — P3). The tag never reaches the browser, only the id (H7).
 */
export interface PendingPartnerAttribution {
  partner_click_id: string;
  capture_method: 'deep_link';
  signup_platform: 'web';
}

export interface PendingAttribution {
  signup_platform?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer_url?: string;
  partner?: PendingPartnerAttribution;
  captured_at: number;
}

/** `?pc=<click id>` → the partner block, or undefined for anything malformed. */
export function partnerFromLandingParams(params: URLSearchParams): PendingPartnerAttribution | undefined {
  const pc = params.get('pc');
  if (!isClickId(pc)) return undefined;
  return { partner_click_id: pc, capture_method: 'deep_link', signup_platform: 'web' };
}

/** First source sticks on the client too: a stored partner click id is never
 *  replaced by a later one; a record without one gains the new one. */
export function mergePartner(
  existing: PendingAttribution | undefined,
  incoming: PendingPartnerAttribution | undefined,
): PendingPartnerAttribution | undefined {
  if (existing?.partner && isClickId(existing.partner.partner_click_id)) return existing.partner;
  return incoming;
}

/**
 * Read UTM + referrer from the current URL and persist to storage.
 *
 * Idempotent: if a pending attribution is already stored AND the current URL
 * has no UTMs, we keep the existing one. If the current URL DOES have UTMs,
 * we overwrite (user bounced back to a fresh landing).
 *
 * Call this as early as possible in App.tsx bootstrap — before React Router
 * takes over the URL.
 */
export async function captureAttributionFromLandingURL(): Promise<void> {
  // Only web — native deep links use their own attribution capture path
  // (Play Install Referrer / SKAdNetwork) handled at the native module level.
  if (Platform.OS !== 'web') return;
  if (typeof window === 'undefined' || !window.location) return;

  try {
    const url = new URL(window.location.href);
    const p = url.searchParams;

    const incomingPartner = partnerFromLandingParams(p);

    const candidate: PendingAttribution = {
      signup_platform: 'web',
      utm_source: p.get('utm_source') || undefined,
      utm_medium: p.get('utm_medium') || undefined,
      utm_campaign: p.get('utm_campaign') || undefined,
      utm_content: p.get('utm_content') || undefined,
      utm_term: p.get('utm_term') || undefined,
      referrer_url: document?.referrer || undefined,
      captured_at: Date.now(),
    };

    const hasAnyUtm =
      candidate.utm_source ||
      candidate.utm_medium ||
      candidate.utm_campaign ||
      candidate.utm_content ||
      candidate.utm_term;

    const rawExisting = await storage.getItem(STORAGE_KEY);
    let existing: PendingAttribution | undefined;
    try {
      existing = rawExisting ? (JSON.parse(rawExisting) as PendingAttribution) : undefined;
    } catch {
      existing = undefined;
    }

    // A partner click id on the URL is kept like a UTM — it is what a partner
    // lead's desktop signup is credited by. An existing partner id always wins.
    const partner = mergePartner(existing, incomingPartner);
    if (partner) candidate.partner = partner;

    if (hasAnyUtm || incomingPartner) {
      await storage.setItem(STORAGE_KEY, JSON.stringify(candidate));
      return;
    }

    // No UTMs on this visit — only stash referrer if nothing exists yet.
    if (!existing && candidate.referrer_url) {
      await storage.setItem(STORAGE_KEY, JSON.stringify(candidate));
    }
  } catch (e) {
    console.warn('[attributionCapture] capture failed:', e);
  }
}

/**
 * Read and clear the pending attribution. Returns undefined if no pending
 * record exists or if it's older than MAX_AGE_MS.
 *
 * Called from the Auth0 callback right before POSTing to /api/auth0/verify.
 */
export async function readAndClearPendingAttribution(): Promise<
  PendingAttribution | undefined
> {
  try {
    const raw = await storage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as PendingAttribution;

    // Age check — drop anything older than a week
    if (
      !parsed.captured_at ||
      Date.now() - parsed.captured_at > MAX_AGE_MS
    ) {
      await storage.removeItem(STORAGE_KEY);
      return undefined;
    }

    // Clear so re-auth flows don't double-attribute
    await storage.removeItem(STORAGE_KEY);
    return parsed;
  } catch (e) {
    console.warn('[attributionCapture] read failed:', e);
    return undefined;
  }
}
